import React, { useEffect, useRef, useState } from "react";
import "ol/ol.css";
import Map from "ol/Map";
import TileLayer from "ol/layer/WebGLTile";
import GeoTIFF from "ol/source/GeoTIFF";
import View from "ol/View";
import { getFilePreSignedUrl } from "../../../../utils/api";
import { LoadingIcon } from "../../../../components";
import { blobDate, blobSitePrefix } from "../../../../utils/demoSiteBlob";

const DetailedVisualisationTif = ({
  currentOrg,
  currentSite,
  siteMetadata,
  currentCrop,
  currentGreenhouse,
  currentPlot,
  currentDate,
  tifType,
  isFullscreen = false,
}) => {
  const [cogFileUrl, setCogFileUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const tifTypeLabelMap = {
    detailedVisualisation: "Detailed Visualisation Tif",
    Percentile_90_100: "90 - 100th Percentile Mask Tif",
    Percentile_80_90: "80 - 90th Percentile Mask Tif",
    Percentile_70_80: "70 - 80th Percentile Mask Tif",
  };

  const mapRef = useRef(null); // To reference the div for the map
  const mapInstanceRef = useRef(null);

  const getAndSetPreSignedUrl = async (cogURL) => {
    try {
      const preSignedUrl = await getFilePreSignedUrl(
        currentOrg,
        currentSite.id,
        currentCrop.name,
        cogURL,
        siteMetadata?.CLOUD_STORAGE_NAME || "azure",
        true,
        true,
      );
      setCogFileUrl(preSignedUrl?.data?.data?.url);
    } catch (error) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      currentSite &&
      currentCrop &&
      currentGreenhouse &&
      currentPlot &&
      currentDate &&
      tifType
    ) {
      // Demo/clone sites read another site's blobs (CLOUD_SITE_PREFIX) at that
      // site's dates (CLOUD_DATE_OFFSETS per bay); plain sites are unaffected.
      const baseURL =
        blobSitePrefix(siteMetadata, currentSite) +
        "/" +
        currentCrop.name +
        "/" +
        blobDate(siteMetadata, currentGreenhouse.name, currentDate) +
        "/" +
        currentGreenhouse.name +
        "/" +
        currentPlot.name +
        "/";

      const cogUrl = baseURL + tifType;
      getAndSetPreSignedUrl(cogUrl);
    }
  }, [
    currentSite,
    siteMetadata,
    currentCrop,
    currentGreenhouse,
    currentPlot,
    currentDate,
    tifType,
  ]);

  useEffect(() => {
    if (!cogFileUrl) return;
    setLoading(true);

    const source = new GeoTIFF({
      sources: [
        {
          url: cogFileUrl,
        },
      ],
    });

    // Initialize the map
    const map = new Map({
      target: mapRef.current,
      layers: [
        new TileLayer({
          source: source,
        }),
      ],
      view: new View({
        center: [0, 0],
        zoom: 0,
      }),
    });
    mapInstanceRef.current = map;

    source.getView().then((viewOptions) => {
      map.setView(new View(viewOptions));
      setTimeout(() => {
        map.updateSize();
        setLoading(false);
      }, 5000);
    });
    return () => {
      map.setTarget(null);
      mapInstanceRef.current = null;
    };
  }, [cogFileUrl]);

  useEffect(() => {
    if (!mapInstanceRef.current) return;
    const timeoutId = setTimeout(() => {
      mapInstanceRef.current?.updateSize();
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [isFullscreen, cogFileUrl]);

  return (
    <div
      className={`heat-map w-full rounded-md relative ${
        isFullscreen
          ? "mb-0 flex h-full min-h-0 flex-col p-2 sm:p-1"
          : "mb-5 p-4 shadow-md sm:p-1 hover:shadow-2xl transition-shadow duration-300 transform hover:scale-101"
      }`}
    >
      <h2 className={`font-bold ${isFullscreen ? "mb-2 text-lg" : "mb-3 text-lg"}`}>
        Post Processing {tifTypeLabelMap[tifType]}
      </h2>

      <div
        className={
          isFullscreen
            ? "relative min-h-0 flex-1"
            : `${!loading ? "items-center justify-between mb-3 flex sm:flex-col " : ""}`
        }
      >
        {loading ? (
          <div
            className={`items-center justify-center ${
              isFullscreen ? "flex h-full" : ""
            }`}
          >
            <LoadingIcon height="200px" />
          </div>
        ) : error ? (
          <div
            className={`text-center text-dangerRed font-bold ${
              isFullscreen ? "flex h-full items-center justify-center" : ""
            }`}
          >
            Error fetching or displaying the TIF file.
          </div>
        ) : !cogFileUrl ? (
          <div
            className={`text-center text-dangerRed font-bold ${
              isFullscreen ? "flex h-full items-center justify-center" : ""
            }`}
          >
            No Tif file available for view.
          </div>
        ) : (
          <div
            ref={loading ? null : mapRef}
            className={`map w-full rounded-md ${
              isFullscreen ? "h-full min-h-0" : ""
            }`}
            style={{ height: isFullscreen ? "100%" : "70vh" }}
          />
        )}
      </div>
    </div>
  );
};

export { DetailedVisualisationTif };
